import { useMemo, ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { createAction } from 'redux-actions';
import { ResponseOrError } from '../../errorHandlers/common-handler';
import {
  AvailablePage,
  AvailablePageWithUniqueName,
  EnhancedBillingPage,
} from '../../interfaces/connected-page';
import { EmailAccount, NewEmailAccount } from '../../interfaces/emailAccount';
import { MyLeadsSearchQuery } from '../../interfaces/myLeads';
import {
  AddonSettingsKeyType,
  EnhancedPlan,
} from '../../interfaces/subscription';
import ChatbotModel, { ChannelName } from '../../model/ChatbotModel';
import CompanyModel from '../../model/CompanyModel';
import ConnectedPageModel from '../../model/ConnectedPageModel';
import ConnectionModel from '../../model/ConnectionModel';
import { PromoterModel } from '../../model/PromoterModel';
import { SubscriptionModel } from '../../model/SubscriptionModel';
import { StoreState } from '../../store-interfaces';
import {
  ActivateInactiveBotModalPropsType,
  AfterPageConnect,
  CallToActionType,
  CampaignDisplayType,
  ContactSalesModalProps,
  DialoguePickerModalProps,
  DialoguePickerModalPropsNew,
  FeatureActivationCongratsModalProps,
  GeneralConfirmModalProps,
  GeneralTextModalProps,
  IAddAudienceToAutomationModalProps,
  IConfirmPurchaseLeadsModalProps,
  IConvertLeadsSuccessModalProps,
  IGoogleSpreadsheetModal,
  IImportContactListModal,
  IIncreaseLimitModal,
  INewBillingModal2023Props,
  INewConfirmModal,
  INewInfoModalProps,
  ISendTestEmailModal,
  TestConnectionModalProps,
  UniversalUpgradeModalProps,
  ClarifyWebsiteTrafficModalProps,
  AnalyzeWebsiteTrafficProps,
  BillingLimitsModalProps,
  SelectXRayModalProps,
} from './props';
import { MODALS_TYPE } from './types';
import reduxStore from '../../store';
import { selectIsShopifyContext } from '../../selectors/viewSelectors';
import AudiencesModel from '../../model/AudiencesModel';
import { EspDomainType } from '../../model/EspModel';

const MODAL_STATE = 'MODAL_STATE';

export const START_CLOSE_MODAL = `${MODAL_STATE}__START_CLOSE_MODAL`;
export const FINISH_CLOSE_MODAL = `${MODAL_STATE}__FINISH_CLOSE_MODAL`;

// Always just use OPEN_MODAL for all actions
export const OPEN_MODAL = `${MODAL_STATE}__OPEN_MODAL`;

export const SET_TOUR = `${MODAL_STATE}__SET_TOUR`;

export const SET_CHATBOT_TOUR_STEP = `${MODAL_STATE}__SET_CHATBOT_TOUR_STEP`;

export const startCloseModal = (modalType?: string) => ({
  type: START_CLOSE_MODAL,
  payload: { modalType },
});

export const finishCLoseModal = (modalType?: string, uid?: string) => ({
  type: FINISH_CLOSE_MODAL,
  payload: { modalType, uid },
});

export const openCreateBotModal = (
  companyId: number,
  facebookPageId: number,
  bypassFreePlanCheck?: boolean,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CREATE_CHATBOT,
    modalProps: { companyId, facebookPageId, bypassFreePlanCheck },
  },
});

const openConfirmChangeBotStatusModal = (
  page: ConnectedPageModel,
  botId: number,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONFIRM_CHANGE_BOT_STATUS_MODAL,
    modalProps: { page, botId },
  },
});

const openTestConnectionModal = (modalProps: TestConnectionModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.TEST_CONNECTION_MODAL,
    modalProps,
  },
});

function openGeneralTextModal(props: GeneralTextModalProps) {
  return {
    type: OPEN_MODAL,
    payload: {
      modalType: MODALS_TYPE.GENERAL_TEXT_MODAL,
      modalProps: props,
    },
  };
}

export type OpenDowngradeFacebookPageModalParams = {
  page: EnhancedBillingPage;
  downgradeType: 'disconnecting' | 'transfer';
};

const openDowngradeFacebookPageModal = (
  modalProps: OpenDowngradeFacebookPageModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.DOWNGRADE_FACEBOOK_PAGE_MODAL,
    modalProps,
  },
});

const openDeactivateAffiliateModal = affiliate => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.DEACTIVATE_AFFILIATE_MODAL,
    modalProps: { affiliate },
  },
});

const openDisconnectPageModal = (
  page: ConnectedPageModel,
  instagramMode?: boolean,
  onDisconnect?: () => void,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.DISCONNECT_PAGES_MODAL,
    modalProps: { page, instagramMode, onDisconnect },
  },
});

const openGrantPermissionsModal = (
  permissions: string,
  onCloseCallback?: () => any,
  allowManualFlow?: boolean,
  headerText?: string,
  bodyText?: string,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.GRANT_PERMISSIONS_MODAL,
    modalProps: {
      permissions,
      onCloseCallback,
      allowManualFlow,
      headerText,
      bodyText,
    },
  },
});

export type OpenConfirmDowngradePromoModalParams = {
  page: ConnectedPageModel;
  companyId: number;
};

const openConfirmDowngradePromoModal = (
  modalProps: OpenConfirmDowngradePromoModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONFIRM_DOWNGRADE_PROMO_MODAL,
    modalProps,
  },
});

export type OpenConfirmFbDowngradeModalParams = {
  facebookPage?: ConnectedPageModel;
  subscription: SubscriptionModel;
  plan: EnhancedPlan;
  customCompanyId?: number;
  tabId?: string;
};

const openConfirmFbDowngradeModal = (
  modalProps: OpenConfirmFbDowngradeModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONFIRM_FB_DOWNGRADE_MODAL,
    modalProps,
  },
});

const openSubscribeAfterFreeTrialPromoModal = (
  onSuccessBilling?: () => void,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.SUBSCRIBE_AFTER_FREE_TRIAL_PROMO,
    modalProps: { onSuccessBilling },
  },
});

export type OpenConnectPagesModalParams = {
  company: CompanyModel;
  canSelectMultiplePages?: boolean;
  onError?: (err: ResponseOrError) => void;
  onSuccess?: (page: ConnectedPageModel) => void;
  title?: string;
  standaloneToken?: string;
  destinationPageId?: number;
  connectedPageButton?: {
    text: string;
    onClick: (
      page: AvailablePageWithUniqueName,
      closeModal: () => void,
    ) => void;
  };
};

const openConnectPagesModal = (modalProps: OpenConnectPagesModalParams) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONNECT_PAGES_MODAL,
    modalProps,
  },
});

const openChatbotLimitModal = (bodyText: string, title?: string) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CHATBOT_LIMIT_MODAL,
    modalProps: { bodyText, title },
  },
});

export type OpenPrettyErrorModalParams = {
  detailsForNerds?: string;
  errorMessage?: JSX.Element | string;
  title: string;
  buttonText?: string;
  onClose?: () => void;
  image?: string;
  css?: {
    container?: string;
    image?: string;
    button?: string;
    title?: string;
    description?: string;
  };
  contactSupport?: boolean;
  errorId?: string;
};

export const openAudiencesSyncRestrictionsModal = (error: string) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.AUDIENCES_SYNC_RESTRICTIONS_MODAL,
    modalProps: { error },
  },
});

export const openPrettyErrorModal = (
  modalProps: OpenPrettyErrorModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.PRETTY_ERROR_MODAL,
    modalProps,
  },
});

export type OpenMonthlySendsWarningModalParams = {
  facebookPageOverride?: EnhancedBillingPage;
};

const openMonthlySendsWarningModal = (
  modalProps: OpenMonthlySendsWarningModalParams = {},
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.MONTHLY_SENDS_WARNING_MODAL,
    modalProps,
  },
});

const openCampaignExceedsSendLimitModal = (
  campaignDisplayType: CampaignDisplayType,
  callToActionType: CallToActionType,
  price?: number,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CAMPAIGN_EXCEEDS_SEND_LIMIT_MODAL,
    modalProps: { campaignDisplayType, callToActionType, price },
  },
});

export const openConfirmDeleteChatbotModal = createAction(
  OPEN_MODAL,
  (bot: ChatbotModel, companyId: number) => ({
    modalType: MODALS_TYPE.CONFIRM_DELETE_CHATBOT,
    modalProps: { bot, companyId },
  }),
);

export const openInviteUserModal = createAction(
  OPEN_MODAL,
  (company: CompanyModel, availableSeats: number[]) => ({
    modalType: MODALS_TYPE.INVITE_USER_MODAL,
    modalProps: { company, availableSeats },
  }),
);

export const openDialogueSettingsModal: (
  pageId: number,
  skipMenuLoad?: boolean,
) => any = (pageId: number, skipMenuLoad?: boolean) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.DIALOGUE_SETTINGS_MODAL,
    modalProps: { pageId, skipMenuLoad },
  },
});

export const openUniversalUpgradeLandmineModal: (
  props: UniversalUpgradeModalProps,
) => any = props => {
  const shopifyAction = createAction(OPEN_MODAL, props => ({
    modalType: MODALS_TYPE.NEW_BILLING_MODAL_2023,
    modalProps: {
      landmineName: props.landmineName,
    },
  }));

  const defaultAction = createAction(OPEN_MODAL, props => ({
    modalType: MODALS_TYPE.UNIVERSAL_UPGRADE_LANDMINE,
    modalProps: props,
    landmineName: props.landmineName,
  }));

  const state = reduxStore.getState();
  const isShopify = selectIsShopifyContext(state);

  return isShopify ? shopifyAction(props) : defaultAction(props);
};

export const openRemoveUserModal = createAction(
  OPEN_MODAL,
  (user, company: CompanyModel) => ({
    modalType: MODALS_TYPE.REMOVE_USER_MODAL,
    modalProps: { user, company },
  }),
);

export const openMenuButtonConfigFormModal = (
  button,
  buttonIndex,
  onCreateButton,
  onUpdateButton,
  closeModalOverride,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.MENU_BUTTON_CONFIG_FORM,
    modalProps: {
      button,
      buttonIndex,
      onCreateButton,
      onUpdateButton,
      closeModalOverride,
    },
  },
});

export const openButtonConfigFormModal = (
  button,
  buttonIndex,
  onCreateButton,
  onUpdateButton,
  widget,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.BUTTON_CONFIG_FORM,
    modalProps: { button, buttonIndex, onCreateButton, onUpdateButton, widget },
  },
});

export const openAddEmailModal = modalProps => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ADD_EMAIL_MODAL,
    modalProps,
  },
});

export const openConnectedPageErrorModal = createAction(OPEN_MODAL, data => ({
  modalType: MODALS_TYPE.CONNECTED_PAGE_ERROR_MODAL,
  modalProps: { data },
}));

export const openRefreshAccessTokenErrorModal = createAction(
  OPEN_MODAL,
  ({ error, callback }: { error: string; callback?: () => void }) => ({
    modalType: MODALS_TYPE.REFRESH_ACCESS_TOKEN_ERROR_MODAL,
    modalProps: { error, callback },
  }),
);

export const openImageUploadFormModal = createAction(
  OPEN_MODAL,
  (onNextButtonClick, onCancelButtonClick) => ({
    modalType: MODALS_TYPE.IMAGE_UPLOAD_FORM_MODAL,
    modalProps: { onNextButtonClick, onCancelButtonClick },
  }),
);

export const openGetStartedOnboardingModal = createAction(
  OPEN_MODAL,
  (
    afterPageConnect: AfterPageConnect,
    onboarding: boolean,
    pageInfo: AvailablePage,
  ) => ({
    modalType: MODALS_TYPE.GET_STARTED_ONBOARDING_MODAL,
    modalProps: { afterPageConnect, onboarding, pageInfo },
  }),
);

export const openAddPromoterModal = createAction(
  OPEN_MODAL,
  onSelectPromoterType => ({
    modalType: MODALS_TYPE.ADD_PROMOTER_MODAL,
    modalProps: { onSelectPromoterType },
  }),
);

export type OpenBillingModalParams = {
  plan: EnhancedPlan;
  upgradetoTeam?: boolean;
  facebookPage?: EnhancedBillingPage;
  paywall?: boolean;
  upgradeFromSubscriptionId?: number;
  downgradeFromSubscriptionId?: number;
  landmineOpenId?: number;
  phoneNumber?: string;
  company?: CompanyModel;
  seatsObject?: {
    userSeats: number;
    extraUserSeats: number;
  };
  navigatedFrom?: {
    location: string;
    search: string;
  };
  level?: {
    id: number;
    name: string;
    level: number;
  };
  updateAllotment?: (
    selectedLevel: number,
    subscriptionId: number,
    companyId: number,
  ) => void;
  subscription?: SubscriptionModel;
  callbackFunction?: (
    level: number,
    subscriptionId: number,
    companyId: number,
  ) => void;
  stayOnPage?: boolean;
  smsActivation?: boolean;
  onSuccessBilling?: () => void;
  onPlanCreatedFailed?: () => void;
  isMonthly?: boolean;
  hideAnnualToggle?: boolean;
  skipFetchActivePage?: boolean;
  extraSendsOnly?: boolean;
  alert?: ReactElement;
  manualRedirect?: boolean;
};

const openBillingModal = (modalProps: OpenBillingModalParams) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.BILLING_MODAL,
    modalProps,
  },
});

const openFreeTrialUpgradeModal = (passedPage?: EnhancedBillingPage) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.FREE_TRIAL_UPGRADE_MODAL,
    modalProps: { passedPage },
  },
});

const openFeatureActivationCongratsModal = (
  modalProps: FeatureActivationCongratsModalProps,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.FEATURE_ACTIVATION_CONGRATS_MODAL,
    modalProps,
  },
});

export type OpenSelectTeamPagesModalParams = {
  subscription: SubscriptionModel;
};

const openSelectTeamPagesModal = (
  modalProps: OpenSelectTeamPagesModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.SELECT_TEAM_PAGES_MODAL,
    modalProps,
  },
});

export type AddMorePagesTeamModalParams = {
  subscription: SubscriptionModel;
  plan: EnhancedPlan;
  pagesCount: number;
  facebookPageId?: number;
};
export type ManagePagesModalParams = {
  subscription: SubscriptionModel;
  plan: EnhancedPlan;
  pagesCount: number;
  facebookPageId?: number;
};
export type ConfirmManagePagesModalParams = {
  subscription: SubscriptionModel;
  pagesCount: number;
};
export type SwapPagesModalParams = {
  subscription: SubscriptionModel;
  facebookPage: ConnectedPageModel;
  freePages: ConnectedPageModel[];
};
export type ConfirmSwapPagesModalParams = {
  facebookPage: ConnectedPageModel;
};

const openConfirmSwapPagesModal = (
  modalProps: ConfirmSwapPagesModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONFIRM_SWAP_PAGES_MODAL,
    modalProps,
  },
});

const openConfirmManagePagesModal = (
  modalProps: ConfirmManagePagesModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONFIRM_MANAGE_PAGES_MODAL,
    modalProps,
  },
});

const openManagePagesModal = (modalProps: ManagePagesModalParams) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.MANAGE_PAGES_MODAL,
    modalProps,
  },
});

const openAddMorePagesTeamModal = (
  modalProps: AddMorePagesTeamModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ADD_MORE_PAGES_TEAM_MODAL,
    modalProps,
  },
});

export const openCreateCompanyModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CREATE_COMPANY_MODAL,
  },
});

export const openContactsExportModal = (
  audienceId: number | null,
  contactsCount?: number,
  onSuccess?: () => void,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONTACTS_EXPORT_MODAL,
    modalProps: { audienceId, contactsCount, onSuccess },
  },
});

export const openVariableValuesModal = (
  toggle,
  onFinish,
  onUpdateFilter,
  selectedFilter,
  selectedFilterId,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.OPEN_VARIABLE_FILTER_MODAL,
    modalProps: {
      toggle,
      onFinish,
      onUpdateFilter,
      selectedFilter,
      selectedFilterId,
    },
  },
});

export const openReachableFilterModal = (
  toggle,
  onFinish,
  onUpdateFilter,
  selectedFilter,
  selectedFilterId,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.OPEN_REACHABLE_FILTER_MODAL,
    modalProps: {
      toggle,
      onFinish,
      onUpdateFilter,
      selectedFilter,
      selectedFilterId,
    },
  },
});

export const openPlatformsFilterModal = (
  toggle,
  onFinish,
  onUpdateFilter,
  selectedFilter,
  selectedFilterId,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.OPEN_PLATFORMS_FILTER_MODAL,
    modalProps: {
      toggle,
      onFinish,
      onUpdateFilter,
      selectedFilter,
      selectedFilterId,
    },
  },
});

export const openNoLinkedAdAccountModal = createAction(OPEN_MODAL, () => ({
  modalType: MODALS_TYPE.NO_LINKED_AD_ACCOUNT_MODAL,
}));

export const openCustomAudienceErrorModal = createAction(OPEN_MODAL, error => ({
  modalType: MODALS_TYPE.CUSTOM_AUDIENCE_ERROR_MODAL,
  modalProps: { error },
}));

export const openShareBotModal = createAction(
  OPEN_MODAL,
  (botSearchToken: string) => ({
    modalType: MODALS_TYPE.SHARE_BOT_MODAL,
    modalProps: { botSearchToken },
  }),
);

export const openConnectPagesFollowUpModal = createAction(OPEN_MODAL, () => ({
  modalType: MODALS_TYPE.CONNECT_PAGES_FOLLOW_UP_MODAL,
  modalProps: {},
}));

export const openPermissionsHelperModal = (
  callback?: () => void,
  mode?: 'discover' | 'grant',
  redirectInitialModal?: string,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.PERMISSIONS_HELPER_MODAL,
    modalProps: { callback, mode, redirectInitialModal },
  },
});

export type OpenActivateInactiveBotModalParams =
  ActivateInactiveBotModalPropsType;

export type OpenActivateChannelsModalParams = {
  channels: ChannelName[];
  onSuccess?: () => void;
  onReject?: (err?: any) => void;
};

const openActivateInactiveBotModal = (
  modalProps?: OpenActivateInactiveBotModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ACTIVATE_INACTIVE_BOT_MODAL,
    modalProps,
  },
});

const openActivateChannelsModal = (
  modalProps?: OpenActivateChannelsModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ACTIVATE_CHANNELS_MODAL,
    modalProps,
  },
});

export const openConfirmSendWelcomeMessageModal = createAction(
  OPEN_MODAL,
  (onConfirm: () => void) => ({
    modalType: MODALS_TYPE.CONFIRM_SEND_WELCOME_MESSAGE_MODAL,
    modalProps: { onConfirm },
  }),
);

export const openLinkAdAccountModal = createAction(OPEN_MODAL, () => ({
  modalType: MODALS_TYPE.LINK_AD_ACCOUNT_MODAL,
}));

export type OpenDeleteConfirmModalParams = {
  headerText: string;
  deleteCallback: () => any;
  newStyle?: boolean;
  deleteButtonName?: string;
  description?: string;
};

const openDeleteConfirmModal = (modalProps: OpenDeleteConfirmModalParams) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONFIRM_DELETE_MODAL,
    modalProps,
  },
});

export type OpenPromoBlasterModalParams = {
  publishCallback: () => any;
  cancelCallback: () => any;
};

const openPromoBlasterModal = (modalProps: OpenPromoBlasterModalParams) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.PROMO_BLASTER_MODAL,
    modalProps,
  },
});

export type OpenEmailSubsmissionModalParams = {
  onStepsComplete: (email?: string) => void;
  onEmailChange: ({
    email,
    isValid,
  }: {
    email: string;
    isValid: boolean;
  }) => void;
  emailState: { email: string; isValid: boolean };
  onClose?: () => void;
};

const openEmailSubmissionModal = (
  modalProps: OpenEmailSubsmissionModalParams,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.EMAIL_SUBMISSION_MODAL,
    modalProps,
  },
});

export const closeModal =
  (modalType?: string) => (dispatch: Dispatch<StoreState>) => {
    dispatch(startCloseModal(modalType));

    setTimeout(() => {
      dispatch(finishCLoseModal(modalType));
    }, 300);
  };

export const openModal = ({ modalType, modalProps }) => ({
  type: OPEN_MODAL,
  payload: {
    modalType,
    modalProps,
  },
});

export const openGeneralConfirmModal = (
  modalProps: GeneralConfirmModalProps,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.GENERAL_CONFIRM_MODAL,
    modalProps,
  },
});

export const openNewConfirmModal = (modalProps: INewConfirmModal) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.NEW_CONFIRM_MODAL,
    modalProps,
  },
});

export type OpenAllSetModalProps = { message: string };

const openAdminAccessRequiredModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ADMIN_ACCESS_REQUIRED,
  },
});

const openAllSetModal = (modalProps: OpenAllSetModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ALL_SET_MODAL,
    modalProps,
  },
});

const openMobileDownloadModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.MOBIBLE_DOWNLOAD,
    modalProps: {},
  },
});

const openDialoguePickerModal = (
  modalProps: Pick<DialoguePickerModalProps, 'onDialogueSelected'>,
) => ({
  type: OPEN_MODAL,
  payload: { modalType: MODALS_TYPE.DIALOGUE_PICKER_MODAL, modalProps },
});

const openDialoguePickerNewModal = (
  modalProps: DialoguePickerModalPropsNew,
) => ({
  type: OPEN_MODAL,
  payload: { modalType: MODALS_TYPE.DIALOGUE_PICKER_NEW_MODAL, modalProps },
});

const openImportContactListModal = (
  modalProps?: Partial<IImportContactListModal>,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.IMPORT_CONTACT_LIST_MODAL,
    modalProps,
  },
});

const openBotResponseSettingsModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.BOT_RESPONSE_SETTINGS_MODAL,
  },
});

const openChatWidgetPreviewModal = (chatWidget: PromoterModel) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CHAT_WIDGET_PREVIEW_MODAL,
    modalProps: {
      chatWidget,
    },
  },
});

export type ExportModalProps = {
  title: string;
  description: string;
  onExport: (email: string) => void;
};

const openExportModal = (props: ExportModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.OPEN_EXPORT_MODAL,
    modalProps: props,
  },
});

export type InstagramConnectModalProps = {
  startStep?: number;
  showCongratulations?: boolean;
  eventName?: string;
  onInstagramConnectError?: (err?: any) => void;
  onSuccessInstagramConnect?: () => void;
  otherFacebookPage?: EnhancedBillingPage;
};

const openInstagramConnectModal = (props?: InstagramConnectModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.OPEN_INSTAGRAM_CONNECT_MODAL,
    modalProps: props,
  },
});

export type ActivateSMSNumberModalProps = {
  startStep?: number;
  onSuccessActivateNumber?: () => void;
  otherFacebookPage?: EnhancedBillingPage;
};

const openActivateSMSNumberModal = (props?: ActivateSMSNumberModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.OPEN_ACTIVATE_SMS_NUMBER_MODAL,
    modalProps: props,
  },
});

const openDec2020FbApiEuropeChangesModal = ({
  onClose,
}: {
  onClose: () => void;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.DEC_2020_FB_API_EUROPE_CHANGES_MODAL,
    modalProps: { onClose },
  },
});
const openSpreadsheetTestConnectionModal = (modalProps: {
  connection: ConnectionModel;
  onClickActivate: () => void;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.SPREADSHEET_TEST_CONNECTION_MODAL,
    modalProps,
  },
});

const openConfirmStoryMentionModal = (createStarter: () => void) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONFIRM_STORY_MENTION_MODAL,
    modalProps: { createStarter },
  },
});

const openInstagramMediaModal = (promoter: PromoterModel) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.INSTAGRAM_MEDIA_MODAL,
    modalProps: { promoter },
  },
});

const openMailchimpTestConnectionModal = (modalProps: {
  connection: ConnectionModel;
  onClickActivate: () => void;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.MAILCHIMP_TEST_CONNECTION_MODAL,
    modalProps,
  },
});

const openSubscriptionPausingModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.SUBSCRIPTION_PAUSING_MODAL,
  },
});

const openMigrateToInstachampThanksModal = (modalProps: {
  onConfirmHandler: () => void;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.MIGRATE_TO_INSTACHAMP_THANKS_MODAL,
    modalProps,
  },
});

const openNewAudienceNamePromptModal = (modalProps: {
  onConfirmHandler: (name: string) => void;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.NEW_AUDIENCE_NAME_PROMPT_MODAL,
    modalProps,
  },
});

const openSelectFacebookPageModal = (modalProps: {
  pages: EnhancedBillingPage[];
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.SELECT_FACEBOOK_PAGE_MODAL,
    modalProps,
  },
});

const openAudienceExtractorModal = (modalProps: {
  channel: 'instagram' | 'facebook' | undefined;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.AUDIENCE_EXTRACTOR_MODAL,
    modalProps,
  },
});
const openSendTestEmailModal = (modalProps: ISendTestEmailModal) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.SEND_TEST_EMAIL_MODAL,
    modalProps,
  },
});

const openConnectChannelModal = (modalProps: {
  channels;
  facebookPage: EnhancedBillingPage;
  title?: string;
  subTitle?: string;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONNECT_CHANNEL_MODAL,
    modalProps,
  },
});

const openActivateEmailAccountModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ACTIVATE_EMAIL_ACCOUNT_MODAL,
  },
});

const openEmailAccountEditorModal = (modalProps: {
  emailAccountUnderEdit: undefined | NewEmailAccount | EmailAccount;
  onClose?: () => void;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.EMAIL_ACCOUNT_EDITOR_MODAL,
    modalProps,
  },
});

const openVerifyEmailModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.VERIFY_EMAIL_MODAL,
  },
});

const openCloneAutomationModal = (modalProps: {
  campaignId?: number;
  showCongratulation?: boolean;
  retrieveCampaigns?: boolean;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CLONE_AUTOMATION,
    modalProps,
  },
});

const openImportAutomationModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.IMPORT_AUTOMATION,
  },
});

const openIncreaseEmailLimitModal = (modalProps: IIncreaseLimitModal) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.INCREASE_EMAIL_LIMIT,
    modalProps,
  },
});

const openGoogleSpreadSheetModal = (modalProps: IGoogleSpreadsheetModal) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.GOOGLE_SPREADSHEET_MODAL,
    modalProps,
  },
});

const openAddAudienceToAutomationModal = (
  modalProps: IAddAudienceToAutomationModalProps,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ADD_AUDIENCE_TO_AUTOMATION,
    modalProps,
  },
});

const openConvertLeadsSuccessModal = (
  modalProps: IConvertLeadsSuccessModalProps,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONVERTED_LEADS_SUCCCESS_MODAL,
    modalProps,
  },
});

const openConfirmPurchaseLeadsModal = (
  modalProps: IConfirmPurchaseLeadsModalProps,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONVERT_LEADS_TO_CONTACTS,
    modalProps,
  },
});

const openContactSalesModal = (modalProps: ContactSalesModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONTACT_SALES,
    modalProps,
  },
});

const openEditWebsiteURLModal = (modalProps: {
  onClose: () => void;
  hideClose?: boolean;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.EDIT_WEBSITE_URL,
    modalProps,
  },
});

const openNewBillingModal2023 = (modalProps: INewBillingModal2023Props) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.NEW_BILLING_MODAL_2023,
    modalProps,
  },
});

const openSignupXRayModal = (modalProps: {
  onClose: () => void;
  website: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.SIGN_UP_X_RAY_MODAL,
    modalProps,
  },
});

const openAnalyzeWebsiteTrafficModal = (
  modalProps: AnalyzeWebsiteTrafficProps,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ANALYZE_WEBSITE_TRAFFIC_MODAL,
    modalProps,
  },
});

const openPromoteInstachampModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.PROMOTE_INSTACHAMP_MODAL,
  },
});

const openClarifyWebsiteTrafficModal = (
  modalProps: ClarifyWebsiteTrafficModalProps,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CLARIFY_WEBSITE_TRAFFIC_MODAL,
    modalProps,
  },
});

const openCreateMyLeadsAudienceModal = (modalProps: {
  onSave: (audienceName: string) => void;
  title?: string;
  defaultName?: string;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CREATE_MY_LEADS_AUDIENCE_MODAL,
    modalProps,
  },
});

const openWelcomeSeriesModal = (modalProps?: {
  onModalClose?: () => void;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.WELCOME_SERIES_MODAL,
    modalProps,
  },
});

const openInvoiceModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.INVOICES_MODAL,
  },
});

const openUserEditModal = (modalProps: {
  companyId: number;
  userId: number;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.EDIT_USER_MODAL,
    modalProps,
  },
});

const openBillingPriceTableModal = (
  type: AddonSettingsKeyType,
  pageId: number,
  plan?: EnhancedPlan,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.BILLING_PRICE_TABLE_MODAL,
    modalProps: { type, pageId, plan },
  },
});

const openNewInfoModal = (modalProps: INewInfoModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.NEW_INFO_MODAL,
    modalProps,
  },
});

const openBillingLimitsModal = (modalProps: BillingLimitsModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.BILLING_LIMITS_MODAL,
    modalProps,
  },
});

const openConnectSendGridModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONNECT_SENDGRID_MODAL,
  },
});

const openAccountLockedModal = () => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ACCOUNT_IS_LOCKED_MODAL,
  },
});

const openIntegrationMappingModal = (modalProps: {
  connection: ConnectionModel;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.INTEGRATION_MAPPING_MODAL,
    modalProps,
  },
});

const openSelectXrayModal = (modalProps: SelectXRayModalProps) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.SELECT_XRAY_MODAL,
    modalProps,
  },
});

const openConnectShopifyModal = (modalProps: {
  onConnect: (shop: string, accessToken: string) => void;
  defaultShop?: string;
  defaultAccessToken?: string;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONNECT_SHOPIFY_MODAL,
    modalProps,
  },
});

const openConversionURLModal = (modalProps?: {
  audience?: AudiencesModel;
  setFilterParams?: (params: MyLeadsSearchQuery) => void;
}) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.CONVERSION_URL_MODAL,
    modalProps,
  },
});

const openAddPromocodeModal = (
  facebookPageId: number,
  companyId: number,
  defaultPromocode?: string,
) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.ADD_PROMOCODE_MODAL,
    modalProps: { defaultPromocode, facebookPageId, companyId },
  },
});

const openValidateDNSModal = (modalProps: { domain: EspDomainType }) => ({
  type: OPEN_MODAL,
  payload: {
    modalType: MODALS_TYPE.VALIDATE_DNS_MODAL,
    modalProps,
  },
});

const modalActions = {
  openCreateBotModal,
  openConfirmDeleteChatbotModal,
  openInviteUserModal,
  openConnectPagesModal,
  openUniversalUpgradeLandmineModal,
  openRemoveUserModal,
  openMenuButtonConfigFormModal,
  openButtonConfigFormModal,
  openAddEmailModal,
  openTestConnectionModal,
  openDeactivateAffiliateModal,
  openDisconnectPageModal,
  openConfirmDowngradePromoModal,
  openConnectedPageErrorModal,
  openRefreshAccessTokenErrorModal,
  openImageUploadFormModal,
  openGetStartedOnboardingModal,
  openAddPromoterModal,
  openCreateCompanyModal,
  openContactsExportModal,
  openConfirmChangeBotStatusModal,

  // TODO MM4580 - consider removal
  openVariableValuesModal,
  openPlatformsFilterModal,
  openReachableFilterModal,

  openDowngradeFacebookPageModal,
  openNoLinkedAdAccountModal,
  openCustomAudienceErrorModal,
  openGeneralTextModal,
  openShareBotModal,
  openActivateInactiveBotModal,
  openActivateChannelsModal,
  closeModal,
  openModal,
  openConfirmFbDowngradeModal,
  openConfirmSendWelcomeMessageModal,
  openGeneralConfirmModal,
  openNewConfirmModal,
  openLinkAdAccountModal,
  openDeleteConfirmModal,
  openPromoBlasterModal,
  openGrantPermissionsModal,
  openPrettyErrorModal,
  openChatbotLimitModal,
  openBillingModal,
  openFreeTrialUpgradeModal,
  openFeatureActivationCongratsModal,
  openMonthlySendsWarningModal,
  openCampaignExceedsSendLimitModal,
  openSelectTeamPagesModal,
  openAddMorePagesTeamModal,
  openManagePagesModal,
  openConfirmManagePagesModal,
  openConfirmSwapPagesModal,
  openAllSetModal,
  openMobileDownloadModal,
  openDialoguePickerModal,
  openDialoguePickerNewModal,
  openImportContactListModal,
  openBotResponseSettingsModal,
  openChatWidgetPreviewModal,
  openDialogueSettingsModal,
  openExportModal,
  openInstagramConnectModal,
  openPermissionsHelperModal,
  openAudiencesSyncRestrictionsModal,
  openConnectPagesFollowUpModal,
  openDec2020FbApiEuropeChangesModal,
  openEmailSubmissionModal,
  openAdminAccessRequiredModal,
  openSpreadsheetTestConnectionModal,
  openConfirmStoryMentionModal,
  openInstagramMediaModal,
  openMailchimpTestConnectionModal,
  openSubscriptionPausingModal,
  openMigrateToInstachampThanksModal,
  openNewAudienceNamePromptModal,
  openActivateSMSNumberModal,
  openSelectFacebookPageModal,
  openAudienceExtractorModal,
  openSendTestEmailModal,
  openConnectChannelModal,
  openActivateEmailAccountModal,
  openEmailAccountEditorModal,
  openSubscribeAfterFreeTrialPromoModal,
  openVerifyEmailModal,
  openCloneAutomationModal,
  openImportAutomationModal,
  openIncreaseEmailLimitModal,
  openGoogleSpreadSheetModal,
  openAddAudienceToAutomationModal,
  openConfirmPurchaseLeadsModal,
  openConvertLeadsSuccessModal,
  openContactSalesModal,
  openNewBillingModal2023,
  openEditWebsiteURLModal,
  openSignupXRayModal,
  openAnalyzeWebsiteTrafficModal,
  openPromoteInstachampModal,
  openClarifyWebsiteTrafficModal,
  openCreateMyLeadsAudienceModal,
  openWelcomeSeriesModal,
  openInvoiceModal,
  openUserEditModal,
  openBillingPriceTableModal,
  openNewInfoModal,
  openBillingLimitsModal,
  openConnectSendGridModal,
  openAccountLockedModal,
  openIntegrationMappingModal,
  openSelectXrayModal,
  openConnectShopifyModal,
  openConversionURLModal,
  openAddPromocodeModal,
  openValidateDNSModal,
};

export default modalActions;

/**
 * Dynamically build a hook for modals.
 */

type ModalActionType = keyof typeof modalActions;
const modalActionsNames = Object.keys(modalActions) as ModalActionType[];

type ModalActionsMap = {
  [P in ModalActionType]: (
    ...args: Parameters<typeof modalActions[P]>
  ) => Promise<void>;
};
export const useModalActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  return useMemo(
    () =>
      modalActionsNames.reduce(
        (acc, k) => ({
          ...acc,
          [k]: (...args: any[]) => dispatch((modalActions as any)[k](...args)),
        }),
        {} as ModalActionsMap,
      ),
    [dispatch],
  );
};
